import { Logger } from "#/lib/logging";
import { getValidSupabaseClient, GetValidSupabaseClient } from "#/lib/supabase";
import { supabaseThen, supabaseThenResult } from "#/lib/supabase/supabase-then";
import { isBrowser, isDevEnvironment } from "#/utils/environment";
import { UTCDate } from "@date-fns/utc/date";
import { startOfMonth } from "date-fns/startOfMonth";
import { endOfMonth } from "date-fns/endOfMonth";
import { addMonths } from "date-fns";
import { differenceInMonths } from "date-fns/differenceInMonths";
import { getStripeProducts } from "#next/pages/api/stripe/products.action";

type CommandsLeftArgs =
  & GetValidSupabaseClient<"BROWSER" | "IMPERSONATION" | "SERVER">
  & {
    userId: string
  };

export type CommandsLeft =
  | {
    isFreeTier: false,
    stripeCustomerId: string,
    recurringCommandsRemaining: number,
    hasReachedRecurringLimit: boolean,
    numCommandsForTier: number,
    hasUnitPricing: boolean,
    commandsUsed: number,
    tier: "pro" | "enterprise" | "free"
  }
  | {
    isFreeTier: true,
    stripeCustomerId: null,
    recurringCommandsRemaining: number,
    hasReachedRecurringLimit: boolean,
    numCommandsForTier: number,
    hasUnitPricing: false,
    commandsUsed: number,
    tier: "pro" | "enterprise" | "free"
  };

export const commandsLeft = async ({
  userId,
  ...supabaseArgs
}: CommandsLeftArgs): Promise<Result<CommandsLeft>> => {
  const supabase = getValidSupabaseClient(supabaseArgs);

  const {
    data: userSubscription, error: userSubscriptionsError
  } = await supabase.from("user_subscriptions")
    .select("*")
    .eq("user_id", userId)
    .eq("status", "active")
    .limit(1)
    .maybeSingle()
    .then(supabaseThenResult(new Error().stack));
  if (userSubscriptionsError) {
    return err(Logger.error({
      message: "Error checking user subscriptions",
      error: userSubscriptionsError
    }));
  }

  const now = new UTCDate();
  let fromDate: string;
  let toDate: string;
  if (!userSubscription) {
    fromDate = startOfMonth(now).toISOString();
    toDate = endOfMonth(now).toISOString();
  } else {
    const createdAt = new UTCDate(userSubscription.created_at);
    const numMonthsElapsed = differenceInMonths(now, createdAt);
    fromDate = addMonths(createdAt, numMonthsElapsed).toISOString();
    toDate = addMonths(createdAt, numMonthsElapsed + 1).toISOString();
  }

  const {
    count: numCommandsUsed, error: numCommandsUsedError
  } = await supabase.from("commands")
    .select("id", { count: "exact" })
    .eq("user_id", userId)
    .gte("created_at", fromDate)
    .lte("created_at", toDate)
    .then(supabaseThen(new Error().stack));

  if (numCommandsUsedError || numCommandsUsed == null) {
    return err(Logger.error({
      message: "Error getting command count",
      error: numCommandsUsedError ?? new Error("No command count")
    }));
  }

  let tier: "pro" | "enterprise" | "free";
  const isFreeTier = userSubscription == null;
  let commandsUsageLimit: number;
  let hasUnitPricing: boolean;
  if (isFreeTier) {
    commandsUsageLimit = 75;
    hasUnitPricing = false;
    tier = "free";
  } else {
    const stripeProducts = await getStripeProducts();
    const stripeProduct = stripeProducts.find(product => product.id === userSubscription.product_id);
    if (!stripeProduct) {
      return err(Logger.error({
        message: "No stripe product found",
        error: new Error("No stripe product found")
      }));
    }
    hasUnitPricing = stripeProduct.hasMeteredPrice;
    commandsUsageLimit = stripeProduct.numCommands;
    tier = stripeProduct.tier;
  }

  if (isDevEnvironment() && !isBrowser()) {
    if (userSubscription?.stripe_customer_id) {
      return ok({
        isFreeTier: false as const,
        stripeCustomerId: userSubscription.stripe_customer_id,
        hasUnitPricing: true,
        numCommandsForTier: 12,
        recurringCommandsRemaining: 0,
        hasReachedRecurringLimit: true,
        commandsUsed: 0,
        tier: tier
      });
    }
    return ok({
      isFreeTier: true as const,
      stripeCustomerId: null,
      hasUnitPricing: false as const,
      numCommandsForTier: commandsUsageLimit,
      recurringCommandsRemaining: commandsUsageLimit,
      hasReachedRecurringLimit: false,
      commandsUsed: 0,
      tier: tier
    });
  }

  if (isFreeTier) {
    return ok({
      isFreeTier: true as const,
      stripeCustomerId: null,
      hasUnitPricing: false as const,
      numCommandsForTier: commandsUsageLimit,
      recurringCommandsRemaining: commandsUsageLimit,
      hasReachedRecurringLimit: numCommandsUsed >= commandsUsageLimit,
      commandsUsed: numCommandsUsed,
      tier: tier
    });
  }
  return ok({
    isFreeTier: false as const,
    stripeCustomerId: userSubscription.stripe_customer_id,
    hasReachedRecurringLimit: numCommandsUsed >= commandsUsageLimit,
    numCommandsForTier: commandsUsageLimit,
    commandsUsed: numCommandsUsed,
    recurringCommandsRemaining: Math.max(0, commandsUsageLimit - numCommandsUsed),
    hasUnitPricing: hasUnitPricing,
    tier: tier
  });
};